// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { formId } from "@conklin/Overrides/Widgets/ContactUs/ContactForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    deliveryLocation: state.components.contactUsForm.fieldValues["deliveryLocation"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DeliveryLocationStyles {
    deliveryLocationTextField?: TextFieldPresentationProps;
}

export const deliveryLocationStyles: DeliveryLocationStyles = {};

const styles = deliveryLocationStyles;

const DeliveryLocation: React.FC<Props> = ({ fields, deliveryLocation, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [deliveryLocationErrorMessage, setDeliveryLocationErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.deliveryLocation = validateDeliveryLocation;
        if (typeof deliveryLocation !== "undefined") {
            validateDeliveryLocation();
        }
    }, [deliveryLocation]);

    React.useEffect(() => {
        contactFormContext.validators.deliveryLocation = validateDeliveryLocation;
    }, [isRequired]);

    const validateDeliveryLocation = () => {
        const errorMessage = isRequired && !deliveryLocation ? (siteMessage("Field_Required", label) as string) : "";
        setDeliveryLocationErrorMessage(errorMessage);
        return !errorMessage;
    };

    const deliveryLocationChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "deliveryLocation", value: event.target.value });
    };

    return (
        <TextField
            {...styles.deliveryLocationTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={deliveryLocation || ""}
            error={deliveryLocationErrorMessage}
            onChange={deliveryLocationChangeHandler}
            type="text"
            {...{ formId }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DeliveryLocation),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Delivery Location",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Delivery Location",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
